import React, { createContext, useContext, useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';

const ExpertContext = createContext();

export const useExpertContext = () => {
  const context = useContext(ExpertContext);
  if (!context) {
    throw new Error('useExpertContext must be used within an ExpertProvider');
  }
  return context;
};

export const ExpertProvider = ({ children }) => {
  const [experts, setExperts] = useState([]);
  const [filteredExperts, setFilteredExperts] = useState([]);
  const [loading, setLoading] = useState(false);

  // Initial demo data
  useEffect(() => {
    const initialData = [
      {
        id: '1',
        full_name: 'Ahmad Hassan',
        email: 'ahmad.hassan@example.com',
        governorate: 'Amman',
        city: 'Amman',
        latitude: 31.9454,
        longitude: 35.9284,
        support_topics: ['Classroom Setup', 'Lesson Planning'],
        bio: 'Expert in STEM integration.',
        status: 'approved',
        created_at: new Date().toISOString()
      },
      {
        id: '2',
        full_name: 'Layla Mahmoud',
        email: 'layla.mahmoud@example.com',
        governorate: 'Balqa',
        city: 'Salt',
        latitude: 32.0393,
        longitude: 35.7277,
        support_topics: ['Curriculum Design'],
        status: 'pending',
        created_at: new Date().toISOString()
      }
    ];
    setExperts(initialData);
  }, []);

  // Update filtered list whenever experts change
  useEffect(() => {
    setFilteredExperts(experts.filter(e => e.status === 'approved'));
  }, [experts]);

  const addExpert = async (expertData) => {
    const newExpert = {
      id: uuidv4(),
      ...expertData,
      status: 'pending', // Explicitly set to pending
      created_at: new Date().toISOString()
    };
    
    setExperts(prev => [...prev, newExpert]);
    return newExpert;
  };

  const updateExpertStatus = async (expertId, status) => {
    setExperts(prev => prev.map(e => 
      e.id === expertId ? { ...e, status } : e
    ));
  };

  const searchExperts = ({ searchTerm, governorate, topic }) => {
    let results = experts.filter(e => e.status === 'approved');

    if (searchTerm) {
      results = results.filter(e => e.full_name.toLowerCase().includes(searchTerm.toLowerCase()));
    }
    if (governorate) {
      results = results.filter(e => e.governorate === governorate);
    }
    if (topic) {
      results = results.filter(e => e.support_topics?.includes(topic));
    }

    setFilteredExperts(results);
  };

  return (
    <ExpertContext.Provider value={{ experts, filteredExperts, addExpert, updateExpertStatus, searchExperts, loading }}>
      {children}
    </ExpertContext.Provider>
  );
};